import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { setUser } from '../../features/account/accountSlice';
import {
  setCartRestaurant,
  setCustomerOrder,
} from '../../features/customer/cart/cartSlice';
import { getCurrentCustomerRestaurant } from '../data/helpers/customerRestaurantHelpers';
import { Order } from '../data/models/orders/order';
import { ClaimType } from '../data/auth/claimType';
import { Roles } from '../data/auth/roles';

export interface AppState {
  initialized: boolean;
  status: 'idle' | 'loading' | 'failed';
}

const initialState: AppState = {
  initialized: false,
  status: 'idle',
};

export const initializeApp = createAsyncThunk(
  'app/initialize',
  async (_, { dispatch }) => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        if (user.accessToken) {
          const claims = JSON.parse(atob(user.accessToken.split('.')[1]));
          const roles: Roles = {
            isSuperAdmin: claims[ClaimType.SuperAdminClaimType] ? true : false,
            restaurantManager: [],
            kitchenStaff: [],
            restaurantWaiter: [],
          };

          if (claims[ClaimType.RestaurantManagerClaimType]) {
            const restaurantIds = Array.isArray(
              claims[ClaimType.RestaurantManagerClaimType]
            )
              ? claims[ClaimType.RestaurantManagerClaimType]
              : [claims[ClaimType.RestaurantManagerClaimType]];
            roles.restaurantManager = restaurantIds.map((id) => ({
              restaurantId: id,
            }));
          }

          if (claims[ClaimType.RestaurantWaiterClaimType]) {
            const restaurantIds = Array.isArray(
              claims[ClaimType.RestaurantWaiterClaimType]
            )
              ? claims[ClaimType.RestaurantWaiterClaimType]
              : [claims[ClaimType.RestaurantWaiterClaimType]];
            roles.restaurantWaiter = restaurantIds.map((id) => ({
              restaurantId: id,
            }));
          }

          if (claims[ClaimType.KitchenStaffClaimType]) {
            const kitchenStaffClaims = Array.isArray(
              claims[ClaimType.KitchenStaffClaimType]
            )
              ? claims[ClaimType.KitchenStaffClaimType]
              : [claims[ClaimType.KitchenStaffClaimType]];

            roles.kitchenStaff = kitchenStaffClaims.map((claim) => {
              if (claim.includes(':')) {
                const [restaurantId, kitchenIdsString] = claim.split(':');
                const kitchenIds = kitchenIdsString.split(',');
                return {
                  restaurantId,
                  kitchenIds,
                };
              }
              return {
                restaurantId: claim,
                kitchenIds: [],
              };
            });
          }

          dispatch(
            setUser({
              ...user,
              roles: roles,
            })
          );
        }
      }

      // Initialize customer restaurant data
      const customerRestaurant = getCurrentCustomerRestaurant();
      if (customerRestaurant) {
        // Set restaurant info
        dispatch(
          setCartRestaurant({
            id: customerRestaurant.id,
            name: customerRestaurant.name,
          })
        );

        // Set orders if they exist
        if (customerRestaurant.orders) {
          customerRestaurant.orders.forEach((order: Order) => {
            dispatch(
              setCustomerOrder({
                restaurantId: customerRestaurant.id,
                restaurantName: customerRestaurant.name,
                order,
              })
            );
          });
        }
      }

      return true;
    } catch (error) {
      console.error('App initialization failed:', error);
      throw error;
    }
  }
);

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(initializeApp.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(initializeApp.fulfilled, (state) => {
        state.status = 'idle';
        state.initialized = true;
      })
      .addCase(initializeApp.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export default appSlice.reducer;
